import { useEffect } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const servicesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '픽스코프 서비스 | Fixcope Services',
  url: `${siteUrl}/services`,
  description: '픽스코프의 IT 솔루션 서비스: 클라우드, 사이버보안, AI 분석, 맞춤형 개발, IT 지원, 디지털 전략.',
  inLanguage: 'ko-KR',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Fixcope Services',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Cloud Solutions' },
      { '@type': 'ListItem', position: 2, name: 'Cybersecurity' },
      { '@type': 'ListItem', position: 3, name: 'AI & Analytics' },
      { '@type': 'ListItem', position: 4, name: 'Custom Development' },
      { '@type': 'ListItem', position: 5, name: 'IT Support' },
      { '@type': 'ListItem', position: 6, name: 'Digital Strategy' },
    ],
  },
};

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services to modernize your IT environment',
      details: 'Our cloud solutions help businesses migrate, manage, and optimize their infrastructure across AWS, Azure, and Google Cloud platforms. We provide end-to-end cloud services including architecture design, migration planning, implementation, and ongoing management to ensure your cloud environment is secure, scalable, and cost-effective.',
      features: ['Cloud Migration', 'Infrastructure Management', 'Cost Optimization', 'Multi-Cloud Strategy'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions to protect your business from evolving threats',
      details: 'Protect your organization with our advanced cybersecurity services. We offer threat detection, vulnerability assessments, penetration testing, and security audits to identify and mitigate risks. Our team implements robust security frameworks and provides 24/7 monitoring to safeguard your critical assets and data.',
      features: ['Threat Detection', 'Security Audits', 'Compliance Management', 'Incident Response'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    },
    {
      icon: 'ri-brain-line',
      title: 'AI & Analytics',
      description: 'Harness the power of artificial intelligence and data analytics for smarter decisions',
      details: 'Transform your data into actionable insights with our AI and analytics services. We develop custom machine learning models, implement predictive analytics, and create intelligent automation solutions that drive business growth. Our data scientists work closely with your team to unlock the full potential of your data.',
      features: ['Machine Learning', 'Predictive Analytics', 'Data Visualization', 'Business Intelligence'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    },
    {
      icon: 'ri-code-box-line',
      title: 'Custom Development',
      description: 'Tailored software solutions designed to meet your unique business requirements',
      details: 'We build custom software applications that perfectly align with your business processes and goals. From web and mobile applications to enterprise systems, our experienced developers use cutting-edge technologies to create scalable, maintainable, and user-friendly solutions that give you a competitive advantage.',
      features: ['Web Applications', 'Mobile Apps', 'API Development', 'System Integration'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'IT Support',
      description: '24/7 technical support and maintenance to keep your systems running smoothly',
      details: 'Our dedicated IT support team provides round-the-clock assistance to ensure your technology infrastructure operates at peak performance. We offer proactive monitoring, rapid issue resolution, regular maintenance, and strategic guidance to minimize downtime and maximize productivity across your organization.',
      features: ['24/7 Monitoring', 'Help Desk Support', 'System Maintenance', 'Performance Optimization'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Digital Strategy',
      description: 'Strategic consulting to align technology with your business objectives',
      details: 'Our digital strategy consultants help you navigate the complexities of digital transformation. We assess your current technology landscape, identify opportunities for innovation, and develop comprehensive roadmaps that align IT initiatives with your business goals to drive sustainable growth and competitive advantage.',
      features: ['Digital Transformation', 'Technology Roadmap', 'Process Optimization', 'Change Management'],
      color: 'bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB]',
      iconColor: 'text-[#0066CC]'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="픽스코프 서비스 | Fixcope - 클라우드·보안·AI·디지털 전략"
        description="픽스코프의 종합 IT 솔루션 서비스를 소개합니다. 클라우드 솔루션, 사이버보안, AI 분석, 맞춤형 개발, IT 지원, 디지털 전략으로 비즈니스 혁신을 지원합니다."
        keywords="픽스코프 서비스, IT 솔루션, 클라우드, 사이버보안, AI 분석, 디지털 전략, Fixcope services"
        canonical="/services"
        ogTitle="픽스코프 서비스 | Fixcope"
        ogDescription="클라우드·보안·AI·디지털 전략으로 비즈니스 혁신을 지원하는 픽스코프 서비스."
        lang="ko"
        jsonLd={servicesJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-8 bg-gradient-to-br from-[#0066CC] to-[#0052A3] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center text-white">
            <div className="inline-flex items-center space-x-2 mb-6">
              <i className="ri-service-line text-white/80"></i>
              <span className="text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                Our Services
              </span>
            </div>
            <h1 className="text-6xl font-bold mb-6">
              Comprehensive IT Solutions
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Empowering businesses with cutting-edge technology services designed to drive innovation, enhance efficiency, and accelerate digital transformation
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-8 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                className={`${service.color} rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 p-12">
                  <div>
                    <div className={`w-16 h-16 rounded-xl ${service.iconColor} bg-white flex items-center justify-center mb-6`}>
                      <i className={`${service.icon} text-3xl`}></i>
                    </div>
                    <h2 className="text-4xl font-bold text-[#1A2332] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <p className="text-gray-600 leading-relaxed mb-8">
                      {service.details}
                    </p>
                    <button className="px-8 py-3 bg-[#0066CC] text-white rounded-lg font-semibold hover:bg-[#0052A3] transition-colors whitespace-nowrap">
                      Get Started
                    </button>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A2332] mb-6">
                      Key Features
                    </h3>
                    <div className="space-y-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-4">
                          <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center flex-shrink-0 mt-1">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-[#1A2332] mb-2">
                              {feature}
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                              Professional implementation and ongoing support to ensure optimal performance and results
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-3xl p-16 text-center text-white">
            <h2 className="text-5xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how our services can help you achieve your technology goals and drive business success
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-white text-[#0066CC] rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
                Schedule Consultation
              </button>
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap">
                View Case Studies
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
